import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import {
    atomWithMutation,
    atomWithQuery,
    queryClientAtom,
} from 'jotai-tanstack-query';

import { toast } from '@/components';
import { Log } from '@/shared/Log';
import { FetchError } from '@/shared/fetch';
import { ICaregiverSpaceConsultationMessageDto } from '@/shared/interfaces';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import {
    ICaregiverConsultationDto,
    selectedWaitingRoomRoleAtom,
    userIdAtom,
} from '@/caregiverSpace/shared/model';

import { assignConsultation } from '../api/assignConsultation';
import { endConsultation } from '../api/endConsultation';
import { fetchConsultationDetails } from '../api/fetchConsultationDetails';
import { fetchConsultationMessages } from '../api/fetchConsultationMessages';
import { fetchMessageConsultationTypes } from '../api/fetchMessageConsultationTypes';
import { fetchVideoConsultationTypes } from '../api/fetchVideoConsultationTypes';
import { fetchWaitingRoomTextConsultations } from '../api/fetchWaitingRoomTextConsultations';
import { fetchWaitingRoomVideoConsultations } from '../api/fetchWaitingRoomVideoConsultations';
import { setConsultationDemandingStatus } from '../api/setConsultationDemandingStatus';
import { selectConsultations } from '../lib/selectConsultations';
import {
    CONSULTATIONS_QUERY_KEY,
    CONSULTATION_DETAILS_QUERY_KEY,
    CONSULTATION_MESSAGES_QUERY_KEY,
    MESSAGE_CONSULTATION_TYPES_QUERY_KEY,
    TEXT_CONSULTATIONS_QUERY_KEY,
    TEXT_CONSULTATIONS_REFRESH_INTERVAL,
    VIDEO_CONSULTATIONS_REFRESH_INTERVAL,
    VIDEO_CONSULTATION_TYPES_QUERY_KEY,
} from './consts';
import { MessageDrawerParams } from './types';

export const messageDrawerAtom = atom<MessageDrawerParams>();

export const consultationDetailsAtom = atomFamily((consultationUid: string) =>
    atomWithQuery(() => ({
        queryKey: [CONSULTATION_DETAILS_QUERY_KEY, consultationUid],
        queryFn: () => fetchConsultationDetails(consultationUid),
        enabled: Boolean(consultationUid),
    })),
);

export const showMyConsultationsOnTopAtom = atom<boolean>(true);

export const consultationsAtom = atomWithQuery((get) => ({
    queryKey: [CONSULTATIONS_QUERY_KEY, get(selectedWaitingRoomRoleAtom)],
    queryFn: () =>
        fetchWaitingRoomVideoConsultations(get(selectedWaitingRoomRoleAtom)),
    select: selectConsultations(get(showMyConsultationsOnTopAtom))(
        get(userIdAtom),
    ),
    refetchInterval: VIDEO_CONSULTATIONS_REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
    networkMode: 'always',
}));

export const textConsultationsAtom = atomWithQuery((get) => ({
    queryKey: [TEXT_CONSULTATIONS_QUERY_KEY, get(selectedWaitingRoomRoleAtom)],
    queryFn: async () =>
        fetchWaitingRoomTextConsultations(get(selectedWaitingRoomRoleAtom)),
    select: selectConsultations(get(showMyConsultationsOnTopAtom))(
        get(userIdAtom),
    ),
    refetchInterval: TEXT_CONSULTATIONS_REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
    networkMode: 'always',
}));

export const reserveConsultationAtom = atomFamily(
    (refetchQueryKey: string | null) =>
        atomWithMutation((get) => ({
            mutationFn: async (consultationUid: string) => {
                return await assignConsultation(consultationUid);
            },
            onSuccess: (data: boolean, consultationUid: string) => {
                const videoConsultations = get(consultationsAtom).data || [];
                const textConsultations = get(textConsultationsAtom).data || [];

                const findConsultationIndex = (
                    consultations: ICaregiverConsultationDto[],
                    uid: string,
                ) => consultations.findIndex((c) => c.Uid === uid);

                const videoIndex = findConsultationIndex(
                    videoConsultations,
                    consultationUid,
                );
                const textIndex = findConsultationIndex(
                    textConsultations,
                    consultationUid,
                );

                const logUnreservedConsultationsAbove = (
                    consultations: ICaregiverConsultationDto[],
                    index: number,
                    consultationType: string,
                ) => {
                    if (index > 0) {
                        const unreservedConsultationsAbove = consultations
                            .slice(0, index)
                            .filter((c) => !c.AssignedCaregiver);

                        if (unreservedConsultationsAbove.length > 0) {
                            const reservedConsultation = consultations[index];
                            Log.info(
                                `CaregiverSpace: Reserved consultation not at top of ${consultationType} list`,
                                {
                                    consultationUid,
                                    scheduledTime:
                                        reservedConsultation.ScheduledOn,
                                    unreservedConsultationsAbove:
                                        unreservedConsultationsAbove.length,
                                    consultationType,
                                },
                            );
                        }
                    }
                };

                if (videoIndex > 0) {
                    logUnreservedConsultationsAbove(
                        videoConsultations,
                        videoIndex,
                        'video',
                    );
                }

                if (textIndex > 0) {
                    logUnreservedConsultationsAbove(
                        textConsultations,
                        textIndex,
                        'text',
                    );
                }

                get(queryClientAtom)?.refetchQueries({
                    queryKey: [refetchQueryKey ?? CONSULTATIONS_QUERY_KEY],
                });
            },
            onError: (error) => {
                let message = R.CannotCompleteOperationMessage;
                if (error instanceof FetchError && error.status === 409) {
                    message = R.BookedByAnotherCaregiver;
                } else {
                    Log.error(
                        'CaregiverSpace: Waiting room: AssignConsultation failed',
                        {
                            error,
                        },
                    );
                }
                toast.addToast({
                    type: 'error',
                    message: message,
                    autoClose: false,
                });
            },
        })),
);

export const consultationMessagesAtom = atomFamily((consultationUid: string) =>
    atomWithQuery(() => ({
        queryKey: [CONSULTATION_MESSAGES_QUERY_KEY, consultationUid],
        queryFn: () => fetchConsultationMessages(consultationUid),
    })),
);

export const conditionalConsultationMessagesAtom = atomFamily(
    ({
        consultationUid,
        isVideo,
    }: {
        consultationUid: string;
        isVideo: boolean;
    }) =>
        atom((get) => {
            if (isVideo) {
                return {
                    data: undefined as
                        | ICaregiverSpaceConsultationMessageDto[]
                        | undefined,
                    isPending: false,
                    isError: false,
                };
            }
            return get(consultationMessagesAtom(consultationUid));
        }),
    (prev, curr) =>
        prev.consultationUid === curr.consultationUid &&
        prev.isVideo === curr.isVideo,
);

export const endConsultationAtom = atomWithMutation(() => ({
    mutationFn: async ({
        consultationUid,
    }: {
        consultationUid: string;
    }): Promise<void> => await endConsultation(consultationUid),
    onSuccess: () => {
        toast.addToast({
            type: 'success',
            message: R.ConsultationIsNowClosed,
            autoClose: true,
        });
    },
    onError: (error) => {
        toast.addToast({
            type: 'error',
            message: R.CannotCompleteOperationMessage,
            autoClose: false,
        });
        Log.error('CaregiverSpace: End consultation failed', { error });
    },
}));

export const setConsultationDemandingStatusAtom = atomWithMutation(() => ({
    mutationFn: async ({
        consultationUid,
        isParticularlyDemanding,
    }: {
        consultationUid: string;
        isParticularlyDemanding: boolean;
    }): Promise<void> =>
        await setConsultationDemandingStatus(
            consultationUid,
            isParticularlyDemanding,
        ),
    onSuccess: (_data, { isParticularlyDemanding }) => {
        toast.addToast({
            type: 'success',
            message: isParticularlyDemanding
                ? R.ConsultationMarkedAsDemanding
                : R.ConsultationUnmarkedAsDemanding,
            autoClose: true,
        });
    },
    onError: (error) => {
        toast.addToast({
            type: 'error',
            message: R.CannotCompleteOperationMessage,
            autoClose: false,
        });
        Log.error('CaregiverSpace: Set consultation demanding status failed', {
            error,
        });
    },
}));

export const consultationTypesAtom = atomWithQuery(() => ({
    queryKey: [MESSAGE_CONSULTATION_TYPES_QUERY_KEY],
    queryFn: fetchMessageConsultationTypes,
}));
export const videoConsultationTypesAtom = atomWithQuery(() => ({
    queryKey: [VIDEO_CONSULTATION_TYPES_QUERY_KEY],
    queryFn: fetchVideoConsultationTypes,
}));

const isWaitingAndRelevant =
    (userId: number | undefined) => (consultation: ICaregiverConsultationDto) =>
        consultation.IsInWaitingRoom &&
        (!consultation.AssignedCaregiver ||
            consultation.AssignedCaregiver.UserId === userId);

export const hasWaitingPatientsAtom = atom((get) => {
    const userId = get(userIdAtom);
    const videoConsultations = get(consultationsAtom).data ?? [];
    const isRelevant = isWaitingAndRelevant(userId);
    return videoConsultations.some(isRelevant);
});
